"use client";

/**
 * Registration and security tracking utilities
 */

export interface RegistrationMetadata {
  // Device & browser
  userAgent: string;
  deviceFingerprint: string;
  language: string;
  timezone: string;
  screenResolution: string;

  // Source tracking
  referralSource: string | null;
  referralCode: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;

  // Page info
  landingPage: string;
  currentPage: string;

  // Registration context
  registrationMethod: 'email' | 'google' | 'twitter' | 'facebook' | 'other';
  timestamp: string;

  // GA4 stitching (null when analytics is blocked or consent declined)
  gaClientId: string | null;
  gaSessionId: string | null;

  // Google Ads click ids from the JSCLK cookie (latest ad click, 90 days)
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
}

/**
 * Generate a simple device fingerprint based on browser characteristics
 */
export function generateDeviceFingerprint(): string {
  try {
    const nav = navigator as any;
    const screen = window.screen;

    const components = [
      nav.userAgent || '',
      nav.language || '',
      screen.colorDepth || '',
      screen.width || '',
      screen.height || '',
      new Date().getTimezoneOffset() || '',
      nav.hardwareConcurrency || '',
      nav.deviceMemory || '',
      nav.platform || '',
      nav.vendor || '',
    ];

    // Simple hash function
    let hash = 0;
    const str = components.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36);
  } catch {
    return 'unknown';
  }
}

/**
 * Extract UTM parameters from URL
 */
export function extractUTMParams(url?: string): {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
} {
  try {
    const searchParams = new URLSearchParams(url || window.location.search);
    return {
      utmSource: searchParams.get('utm_source'),
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
      utmTerm: searchParams.get('utm_term'),
      utmContent: searchParams.get('utm_content'),
    };
  } catch {
    return {
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      utmTerm: null,
      utmContent: null,
    };
  }
}

/**
 * Get referral source from document.referrer
 */
export function getReferralSource(): string | null {
  try {
    if (typeof document === 'undefined') return null;
    const referrer = document.referrer;
    if (!referrer) return null;

    // Extract domain from referrer
    try {
      const url = new URL(referrer);
      return url.hostname;
    } catch {
      return referrer;
    }
  } catch {
    return null;
  }
}

/**
 * Get stored landing page from session storage
 */
export function getLandingPage(): string {
  try {
    if (typeof window === 'undefined') return '/';

    // Try to get from session storage
    const stored = sessionStorage.getItem('js_landing_page');
    if (stored) return stored;

    // If not stored, store current path and return it
    const current = window.location.pathname + window.location.search;
    sessionStorage.setItem('js_landing_page', current);
    return current;
  } catch {
    return '/';
  }
}

/**
 * Store UTM parameters in session storage for persistence across pages
 */
export function storeUTMParams(): void {
  try {
    if (typeof window === 'undefined') return;

    const params = extractUTMParams();
    const hasUTM = Object.values(params).some(v => v !== null);

    if (hasUTM) {
      sessionStorage.setItem('js_utm_params', JSON.stringify(params));
    }
  } catch {}
}

/**
 * Get stored UTM parameters from session storage
 */
export function getStoredUTMParams(): ReturnType<typeof extractUTMParams> {
  try {
    if (typeof window === 'undefined') {
      return { utmSource: null, utmMedium: null, utmCampaign: null, utmTerm: null, utmContent: null };
    }

    const stored = sessionStorage.getItem('js_utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}

  return extractUTMParams();
}

/**
 * Collect all registration metadata
 */
export function collectRegistrationMetadata(method: RegistrationMetadata['registrationMethod']): RegistrationMetadata {
  const firstTouch = readFirstTouch();
  const sessionUtm = getStoredUTMParams();
  const hasSessionUtm = Object.values(sessionUtm).some(v => v);
  // The first-touch cookie outlives the tab, so a visitor who landed from ChatGPT
  // and came back in a new tab to sign up still carries the tag.
  const utmParams = hasSessionUtm || !firstTouch ? sessionUtm : {
    utmSource: firstTouch.us || null,
    utmMedium: firstTouch.um || null,
    utmCampaign: firstTouch.uc || null,
    utmTerm: firstTouch.ut || null,
    utmContent: firstTouch.uct || null,
  };
  const clicks = readAdClickIds();

  return {
    // Device & browser
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceFingerprint: generateDeviceFingerprint(),
    language: typeof navigator !== 'undefined' ? (navigator.language || (navigator as any).userLanguage || 'en') : 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    screenResolution: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : '',

    // Source tracking. The LANDING referrer, not this page's: document.referrer on
    // /register is our own page (or empty), which is why ReferralSource was blank on
    // nearly every signup and the admin by-referer view filed ChatGPT as no-referrer.
    referralSource: firstTouch?.r || getStoredExternalReferrer() || getExternalReferrer(),
    referralCode: getStoredReferralCode(),
    utmSource: utmParams.utmSource,
    utmMedium: utmParams.utmMedium,
    utmCampaign: utmParams.utmCampaign,
    utmTerm: utmParams.utmTerm,
    utmContent: utmParams.utmContent,

    // Page info
    landingPage: firstTouch?.lp || getLandingPage(),
    currentPage: typeof window !== 'undefined' ? window.location.pathname : '/register',

    // Registration context
    registrationMethod: method,
    timestamp: new Date().toISOString(),

    // GA4 stitching. Without this the server's Measurement Protocol call sends
    // client_id = our own user id, so GA4 files every signup as a brand-new user
    // with no session, no source and no campaign — which is why sign_up reported
    // 130 events from 8 users and why no channel could be evaluated (2026-08-09).
    gaClientId: getGaClientId(),
    gaSessionId: getGaSessionId(),

    gclid: clicks.gclid,
    gbraid: clicks.gbraid,
    wbraid: clicks.wbraid,
  };
}

// ============================================
// FIRST TOUCH + AD CLICK IDS (first-party cookies, 90 days)
// ============================================

const FIRST_TOUCH_COOKIE = 'JSFT';
const AD_CLICK_COOKIE = 'JSCLK';
const NINETY_DAYS = 60 * 60 * 24 * 90;
const OWN_HOSTS = /(^|\.)juicystarz\.com$|^localhost$/i;

export interface FirstTouch {
  /** external referrer host at landing, '' when none */
  r: string;
  /** landing path + query, capped */
  lp: string;
  us?: string; um?: string; uc?: string; ut?: string; uct?: string;
  /** ISO time of the first touch */
  t: string;
}

export interface AdClickIds { gclid: string | null; gbraid: string | null; wbraid: string | null }

function readCookieJson<T>(name: string): T | null {
  try {
    if (typeof document === 'undefined') return null;
    const row = document.cookie.split('; ').find(r => r.startsWith(name + '='));
    if (!row) return null;
    return JSON.parse(decodeURIComponent(row.slice(name.length + 1))) as T;
  } catch {
    return null;
  }
}

function writeCookieJson(name: string, value: unknown, maxAge: number): void {
  try {
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};path=/;max-age=${maxAge};SameSite=Lax`;
  } catch {}
}

/** Host of document.referrer when it is another site, else null. */
export function getExternalReferrer(): string | null {
  try {
    if (typeof document === 'undefined' || !document.referrer) return null;
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host && !OWN_HOSTS.test(host) ? host : null;
  } catch {
    return null;
  }
}

function getStoredExternalReferrer(): string | null {
  try {
    const v = sessionStorage.getItem('js_referrer');
    return v && !OWN_HOSTS.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function readFirstTouch(): FirstTouch | null {
  return readCookieJson<FirstTouch>(FIRST_TOUCH_COOKIE);
}

/**
 * Record where this browser first came from, once. Never overwritten while the
 * cookie lives, so signup attribution is FIRST touch even when the visitor
 * wanders the site or returns days later.
 */
export function captureFirstTouch(): void {
  try {
    if (typeof window === 'undefined' || readFirstTouch()) return;
    const q = new URLSearchParams(window.location.search);
    const pick = (k: string) => (q.get(k) || '').slice(0, 100) || undefined;
    const ft: FirstTouch = {
      r: getExternalReferrer() || '',
      lp: (window.location.pathname + window.location.search).slice(0, 500),
      us: pick('utm_source'), um: pick('utm_medium'), uc: pick('utm_campaign'),
      ut: pick('utm_term'), uct: pick('utm_content'),
      t: new Date().toISOString(),
    };
    writeCookieJson(FIRST_TOUCH_COOKIE, ft, NINETY_DAYS);
  } catch {}
}

/**
 * Keep the LATEST Google Ads click id (gclid, or gbraid/wbraid on iOS) for 90 days,
 * Ads' own click window. The server uploads the signup and onboarded conversions
 * against it, so cost per onboard comes from Ads itself.
 */
export function captureAdClickIds(): void {
  try {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search);
    const ids: AdClickIds = {
      gclid: (q.get('gclid') || '').slice(0, 256) || null,
      gbraid: (q.get('gbraid') || '').slice(0, 256) || null,
      wbraid: (q.get('wbraid') || '').slice(0, 256) || null,
    };
    if (!ids.gclid && !ids.gbraid && !ids.wbraid) return;
    writeCookieJson(AD_CLICK_COOKIE, { ...ids, t: new Date().toISOString() }, NINETY_DAYS);
  } catch {}
}

export function readAdClickIds(): AdClickIds {
  const c = readCookieJson<AdClickIds>(AD_CLICK_COOKIE);
  return { gclid: c?.gclid || null, gbraid: c?.gbraid || null, wbraid: c?.wbraid || null };
}

/**
 * GA4's client id, read from the _ga cookie.
 * Cookie value looks like "GA1.1.1234567890.1699999999"; the client id is the
 * last two dot-separated segments ("1234567890.1699999999").
 * Returns null when analytics is blocked or consent was declined — the server
 * then falls back to its old behavior rather than sending a bogus id.
 */
export function getGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!m) return null;
    const parts = decodeURIComponent(m[1]).split('.');
    if (parts.length < 4) return null;
    return `${parts[2]}.${parts[3]}`;
  } catch {
    return null;
  }
}

/**
 * GA4's session id for the current measurement id, from the _ga_<ID> cookie.
 * Value looks like "GS1.1.<sessionId>.<sessionNumber>....". Optional: sending it
 * lets GA4 attach the signup to the SESSION that produced it, not just the user.
 */
export function getGaSessionId(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/);
    if (!m) return null;
    const parts = decodeURIComponent(m[1]).split('.');
    return parts.length >= 3 ? parts[2] : null;
  } catch {
    return null;
  }
}

/**
 * Determine hero banner variant from utm_content or sessionStorage.
 * Valid values: 'banner-a', 'banner-b', 'banner-c'. Default: 'banner-a'.
 */
export function getHeroVariant(): 'banner-a' | 'banner-b' | 'banner-c' {
  const VALID: readonly string[] = ['banner-a', 'banner-b', 'banner-c'] as const;
  const KEY = 'js_hero_variant';
  try {
    if (typeof window === 'undefined') return 'banner-a';

    // 1. Check URL param first (fresh click from TrafficStars)
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('utm_content');
    if (fromUrl && VALID.includes(fromUrl)) {
      sessionStorage.setItem(KEY, fromUrl);
      return fromUrl as any;
    }

    // 2. Check sessionStorage (persists across navigations)
    const stored = sessionStorage.getItem(KEY);
    if (stored && VALID.includes(stored)) {
      return stored as any;
    }
  } catch { /* SSR or storage error */ }
  return 'banner-a';
}

/**
 * Capture and store TrafficStars click ID from URL param `ts_click_id`.
 * Returns the stored click ID if available.
 */
export function captureTsClickId(): string | null {
  const KEY = 'js_ts_click_id';
  try {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('ts_click_id');
    if (fromUrl) {
      sessionStorage.setItem(KEY, fromUrl);
      return fromUrl;
    }

    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/**
 * Get stored TrafficStars click ID (for sending with signup).
 */
export function getTsClickId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem('js_ts_click_id');
  } catch {
    return null;
  }
}

/**
 * Capture a referral code from ?ref= URL param and persist it in the JSTAFC cookie.
 * The cookie is set with a 30-day lifetime so attribution survives users who click
 * the link and sign up days later. Cleans the ?ref= from the URL after storing.
 * Returns the captured code (or existing stored code if already present).
 */
export function captureReferralCode(): string | null {
  try {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');

    // Read existing JSTAFC cookie
    const existing = document.cookie
      .split('; ')
      .find(row => row.startsWith('JSTAFC='))
      ?.split('=').slice(1).join('=');

    let cookieData: Record<string, string> = {};
    if (existing) {
      try { cookieData = JSON.parse(decodeURIComponent(existing)); } catch {}
    }

    if (refCode) {
      // Store the code and any UTM params present on the same URL
      cookieData.referral_code = refCode;
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      utmKeys.forEach(key => {
        const val = params.get(key);
        if (val) cookieData[key] = val;
      });

      document.cookie = `JSTAFC=${encodeURIComponent(JSON.stringify(cookieData))};path=/;max-age=2592000;SameSite=Lax`;

      // Clean ?ref= from URL without reloading
      params.delete('ref');
      const newSearch = params.toString();
      const cleanUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);

      return refCode;
    }

    // Return already-stored code if any
    return cookieData.referral_code || null;
  } catch {
    return null;
  }
}

/**
 * Get the referral code stored in the JSTAFC cookie (without triggering URL capture).
 */
export function getStoredReferralCode(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const existing = document.cookie
      .split('; ')
      .find(row => row.startsWith('JSTAFC='))
      ?.split('=').slice(1).join('=');
    if (!existing) return null;
    const data = JSON.parse(decodeURIComponent(existing));
    return data.referral_code || null;
  } catch {
    return null;
  }
}

/**
 * Stash registration metadata in a short-lived cookie so the NextAuth signIn
 * callback (server-side) can forward it to the backend after OAuth redirect.
 * localStorage doesn't reach the server; cookies do.
 */
export function writeRegMetaCookie(metadata: RegistrationMetadata): void {
  try {
    if (typeof document === 'undefined') return;
    const payload = encodeURIComponent(JSON.stringify(metadata));
    // 10 min is enough for the OAuth roundtrip; SameSite=Lax survives the redirect.
    document.cookie = `JSREGMETA=${payload};path=/;max-age=600;SameSite=Lax`;
  } catch {}
}

/**
 * Initialize tracking on app load - store landing page and UTM params
 */
export function initializeTracking(): void {
  try {
    if (typeof window === 'undefined') return;

    // Store landing page if this is the first page view
    if (!sessionStorage.getItem('js_landing_page')) {
      sessionStorage.setItem('js_landing_page', window.location.pathname + window.location.search);
    }

    // Store UTM params if present
    storeUTMParams();

    // First touch (once, 90 days) and the latest Google Ads click id (90 days)
    captureFirstTouch();
    captureAdClickIds();

    // Capture TrafficStars click ID if present
    captureTsClickId();

    // Capture referral code from ?ref= (persists 30 days in JSTAFC cookie)
    captureReferralCode();

    // Store the landing referrer if it is another site (our own host is not a source)
    const referrer = getExternalReferrer();
    if (referrer && !sessionStorage.getItem('js_referrer')) {
      sessionStorage.setItem('js_referrer', referrer);
    }
  } catch {}
}

// ============================================
// GA4 EVENT TRACKING - A/B Test Campaign 2025
// ============================================

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Fire a GA4 event the way plain gtag.js expects.
 *
 * The site loads gtag.js directly (no GTM container), and gtag.js only
 * processes `arguments` objects pushed to dataLayer via a gtag() shim —
 * plain-object pushes ({ event: name, ... }) are silently ignored. Pushing
 * an arguments object also queues safely before the lazyOnload library
 * finishes loading (same shim the inline ga-init script in layout.tsx uses).
 */
export function gaEvent(name: string, params: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag(_cmd: string, _name: string, _params?: Record<string, any>) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  gtag('event', name, params);
}

/**
 * Track GA4 event. Kept as the stable public API for all existing call
 * sites; internals now route through gaEvent() so events actually reach
 * GA4 (the old plain-object dataLayer.push was a no-op under gtag.js).
 */
export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  gaEvent(eventName, parameters);
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[GA4] ${eventName}`, parameters);
  }
};

/**
 * Track landing page view
 */
export const trackCampaignView = (campaignSource: string) => {
  trackEvent('landing_page_view', {
    campaign_source: campaignSource,
    page_path: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

/**
 * Track signup start
 */
export const trackSignupStart = (campaignSource: string, formId: string) => {
  trackEvent('signup_start', {
    campaign_source: campaignSource,
    form_id: formId
  });
};

/**
 * Track profile completion (conversion)
 */
export const trackConversion = (campaignSource: string, conversionType: string) => {
  trackEvent('profile_complete', {
    campaign_source: campaignSource,
    conversion_type: conversionType
  });
};

// Campaign A: Job Board Events
export const trackBrowseJobsClick = () => {
  trackEvent('browse_jobs_click', {
    campaign_source: 'Job Board',
    button_text: 'Browse Jobs Now'
  });
};

// Campaign B: UGC Platform Events
export const trackStartEarningClick = () => {
  trackEvent('start_earning_click', {
    campaign_source: 'UGC'
  });
};

export const trackCreatorSignupStart = () => {
  trackEvent('creator_signup_start', {
    campaign_source: 'UGC',
    user_type: 'creator'
  });
};

export const trackContentUploadComplete = (contentType: string) => {
  trackEvent('content_upload_complete', {
    campaign_source: 'UGC',
    content_type: contentType
  });
};

// Campaign C: Community Events
export const trackJoinCommunityClick = () => {
  trackEvent('join_community_click', {
    campaign_source: 'Community'
  });
};

export const trackForumSignupStart = () => {
  trackEvent('forum_signup_start', {
    campaign_source: 'Community'
  });
};

export const trackFirstPostCreated = (category: string) => {
  trackEvent('first_post_created', {
    campaign_source: 'Community',
    category: category
  });
};

// ============================================
// DIRECT GA4 MEASUREMENT PROTOCOL TRACKING
// ============================================

/**
 * Track page view directly to GA4 Measurement Protocol (server-side friendly)
 * This captures real visitor traffic including anonymous users
 */
export async function trackPageViewToGA4(path?: string, title?: string): Promise<void> {
  try {
    if (typeof window === 'undefined') return;

    const pathname = path || window.location.pathname;
    const pageTitle = title || document.title;
    const sessionId = getTsClickId() || getStoredUTMParams().utmCampaign || 'direct';

    // Fire and forget - don't block user. Same-origin so the Next.js
    // catch-all /api/:path* rewrite forwards to API_BASE_HTTP (works in dev + prod).
    fetch(`/api/campaign/track-page-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path: pathname,
        title: pageTitle,
        referrer: document.referrer || null,
        sessionId: sessionId,
        utmParams: getStoredUTMParams(),
        timestamp: new Date().toISOString()
      })
    }).catch(err => {
      console.error('[GA4 PageView] Failed:', err);
    });
  } catch (err) {
    console.error('[GA4 PageView] Error:', err);
  }
}
